import React from 'react';
import Icon from '../../shared/Icon';
import { ForChip, DisabledAction } from './SharedThreadParts';
import { TIMEOUT_TITLE, RUN_LIMIT_MS } from './AnswerOutcome';
import { formatElapsed } from './ProgressRow';

export const FAILED_SENTENCE = 'This answer couldn’t be generated, so there’s no answer. Nothing partial is shown, because it would be incomplete.';
export const FAILED_TITLE = 'The answer failed';
export const FAILED_TEXT = 'Something went wrong while it was being generated, and nothing was saved.';

// The state note's title, glyph and text. A timeout keeps how long it ran and what to try, as
// the private error state does; a coded refusal keeps its own message.
function noteFor(outcome) {
  if (outcome?.code === 'run_timeout') {
    const ran = formatElapsed(outcome.elapsedMs || RUN_LIMIT_MS);
    return {
      title: TIMEOUT_TITLE,
      glyph: 'clock-countdown',
      text: `It ran for ${ran} without finishing. Try a narrower question, or fewer datasets.`,
    };
  }
  const coded = outcome && outcome.code !== 'error' && outcome.message;
  return { title: FAILED_TITLE, glyph: 'warning-circle', text: coded || FAILED_TEXT };
}

/**
 * Shared Queries handoff 2: a failed answer where other people ask, as Answer Presentation's
 * error state (E8). The answer keeps its ResAI strip and "For" chip, says there's no answer,
 * then a state note. Nothing was stored, so the asker can try again (the note's link or
 * Retry); anyone else is told who can, and their Retry is disabled with that reason. While the
 * asker isn't known yet, there's no chip and no way to try again.
 */
function SharedFailedAnswer({ outcome, askerKnown, askerName, mine, automateNote, onTryAgain, onCopy, disabled }) {
  const onlyAsker = askerKnown && !mine ? `Only ${askerName} can try again` : null;
  const note = noteFor(outcome);
  return (
    <div className="message assistant iter1-failed iter1-failed--shared" data-testid="iter1-failed">
      <div className="message-author">
        <img src="/resai-logo.svg" alt="" className="message-avatar message-avatar--logo" aria-hidden="true" />
        <span className="message-author-name">ResAI</span>
        {askerKnown && <ForChip name={askerName} you={mine} />}
      </div>
      <div className="message-content">
        <div className="message-text"><p>{FAILED_SENTENCE}</p></div>
        <div className="message-actions">
          <button type="button" className="iter1-icon-btn" aria-label="Copy" data-tip="Copy" onClick={() => onCopy(FAILED_SENTENCE)}>
            <Icon name="copy" size={16} />
          </button>
          {mine && (
            <button type="button" className="iter1-icon-btn" aria-label="Retry" data-tip="Retry" disabled={disabled} onClick={onTryAgain}>
              <Icon name="arrow-clockwise" size={16} />
            </button>
          )}
          {onlyAsker && <DisabledAction icon="arrow-clockwise" label="Retry" note={onlyAsker} />}
          {automateNote && <DisabledAction icon="lightning" label="Automate" note={automateNote} />}
        </div>
      </div>
      <div className="iter1-failed-card" role="note">
        <Icon name={note.glyph} size={22} className="iter1-failed-glyph" />
        <div className="iter1-failed-body">
          <h3 className="iter1-failed-title">{note.title}</h3>
          <p className="iter1-failed-text">{note.text}{onlyAsker ? ` ${onlyAsker}.` : ''}</p>
          {mine && (
            <button type="button" className="resui-link iter1-link-button" onClick={onTryAgain} disabled={disabled}>
              Try again
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default SharedFailedAnswer;
